import React from "react";
import {
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaGlobe, FaUserCircle,
  FaGraduationCap, FaCertificate, FaTools, FaBriefcase, FaProjectDiagram,
  FaLayerGroup, FaCheckCircle, FaLanguage, FaAddressCard, FaFileSignature,
} from "react-icons/fa";
import { mapUserToResumeData } from "./resumeDataMapper";
import "./ResumeTemplate.css";

/**
 * <ResumeTemplate />
 *
 * Client Template 1 renderer — implements every row of "05_TEMPLATE_MAP".
 * Section order below follows the sheet's "Sequence" column exactly
 * (1-3, 10-13, 20-23, 30, 40, 50, 60, 70-73, 80, 90, 100-102, 110, 120, 130).
 *
 * Props:
 *   user  - the raw object returned by GET /users/:id/ (res.data.data)
 *   industryLabel / scopeName - optional resolved labels (already fetched
 *           in UserView.jsx via /reference-values/ and /scope-catalogs/)
 */
const ResumeTemplate = React.forwardRef(({ user, industryLabel, scopeName }, ref) => {
  const data = mapUserToResumeData(user, { industryLabel, scopeName });

  const formatMonth = (value) => {
    if (!value) return "Present";
    const [y, m] = value.split("-");
    const d = new Date(Number(y), Number(m) - 1);
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  return (
    <div className="resume-doc" ref={ref}>
      {/* ============ HEADER — Seq 1,2,3 ============ */}
      <header className="resume-header">
        <div className="resume-avatar">
          {data.header.photoUrl ? (
            <img src={data.header.photoUrl} alt={data.header.name} />
          ) : (
            <span>{data.header.initials || <FaUserCircle />}</span>
          )}
        </div>
        <div className="resume-header-text">
          <h1>{data.header.name}</h1>
          <p className="resume-title">{data.header.title}</p>
        </div>
      </header>

      {/* ============ PERSONAL DETAILS — Seq 10-13 ============ */}
      <section className="resume-section resume-personal">
        <div className="resume-personal-item">
          <FaMapMarkerAlt /> <span>{data.personal.location}</span>
        </div>
        <div className="resume-personal-item">
          <FaGlobe /> <span>{data.personal.nationality}</span>
        </div>
        <div className="resume-personal-item">
          <FaPhone /> <span>{data.personal.mobile}</span>
        </div>
        <div className="resume-personal-item">
          <FaEnvelope /> <span>{data.personal.email}</span>
        </div>
      </section>

      {/* ============ PROFILE — Seq 20-23 ============ */}
      <section className="resume-section">
        <h2 className="resume-section-title">Professional Summary</h2>
        <p className="resume-summary">{data.profile.summary}</p>

        <div className="resume-metrics">
          <div className="resume-metric-card">
            <span className="resume-metric-value">{data.profile.totalExperience}</span>
            <span className="resume-metric-label">Total Experience</span>
          </div>
        </div>

        <div className="resume-subsection">
          <h3>Relevant Scope Experience</h3>
          <table className="resume-table">
            <tbody>
              {data.profile.scopeExperience.map((row, i) => (
                <tr key={i}>
                  <td>{row.scope}</td>
                  <td className="resume-table-right">
                    {row.years} yrs {row.months} mo
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="resume-subsection">
          <h3>Verified Field Days</h3>
          <table className="resume-table">
            <tbody>
              {data.profile.verifiedFieldDays.map((row, i) => (
                <tr key={i}>
                  <td>{row.scope}</td>
                  <td className="resume-table-right">{row.days} verified days</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ============ EDUCATION — Seq 30 ============ */}
      <section className="resume-section">
        <h2 className="resume-section-title">
          <FaGraduationCap className="resume-section-icon" /> Education
        </h2>
        {data.education.map((edu, i) => (
          <div className="resume-row" key={i}>
            <div className="resume-row-main">
              <strong>{edu.qualification}</strong>
              <span className="resume-row-sub">{edu.institute}</span>
            </div>
            <div className="resume-row-side">
              {formatMonth(edu.startDate)} – {formatMonth(edu.endDate)}
              {edu.grade ? <span className="resume-row-sub">{edu.grade}</span> : null}
            </div>
          </div>
        ))}
      </section>

      {/* ============ CERTIFICATIONS — Seq 40 ============ */}
      <section className="resume-section">
        <h2 className="resume-section-title">
          <FaCertificate className="resume-section-icon" /> Certifications
        </h2>
        {data.certifications.map((cert, i) => (
          <div className="resume-row" key={i}>
            <div className="resume-row-main">
              <strong>{cert.credential}</strong>
              <span className="resume-row-sub">
                {cert.body} · No. {cert.number}
              </span>
            </div>
            <div className="resume-row-side">
              <span className={`resume-badge ${cert.status === "Current" ? "badge-current" : "badge-expired"}`}>
                {cert.status}
              </span>
              <span className="resume-row-sub">
                {formatMonth(cert.issueDate)} – {formatMonth(cert.expiryDate)}
              </span>
            </div>
          </div>
        ))}
      </section>

      {/* ============ TRAINING — Seq 50 ============ */}
      <section className="resume-section">
        <h2 className="resume-section-title">
          <FaTools className="resume-section-icon" /> Safety / Technical Training
        </h2>
        {data.training.map((t, i) => (
          <div className="resume-row" key={i}>
            <div className="resume-row-main">
              <strong>{t.course}</strong>
              <span className="resume-row-sub">{t.provider}</span>
            </div>
            <div className="resume-row-side">
              {formatMonth(t.completedDate)}
              <span className="resume-row-sub">{t.validity}</span>
            </div>
          </div>
        ))}
      </section>

      {/* ============ EMPLOYMENT HISTORY — Seq 60 ============ */}
      <section className="resume-section">
        <h2 className="resume-section-title">
          <FaBriefcase className="resume-section-icon" /> Employment History
        </h2>
        {data.employment.map((job, i) => (
          <div className="resume-row" key={i}>
            <div className="resume-row-main">
              <strong>{job.title}</strong>
              <span className="resume-row-sub">{job.employer}</span>
              <p className="resume-duties">{job.duties}</p>
            </div>
            <div className="resume-row-side">
              {formatMonth(job.startDate)} – {job.current ? "Present" : formatMonth(job.endDate)}
            </div>
          </div>
        ))}
      </section>

      {/* ============ PROJECT EXPERIENCE — Seq 70-73 ============ */}
      <section className="resume-section">
        <h2 className="resume-section-title">
          <FaProjectDiagram className="resume-section-icon" /> Project Experience
        </h2>
        {data.projects.map((proj, i) => (
          <div className="resume-project" key={i}>
            <div className="resume-row">
              <div className="resume-row-main">
                <strong>{proj.projectName}</strong>
                <span className="resume-row-sub">
                  {proj.clientVisibility === "CONFIDENTIAL_DO_NOT_DISPLAY"
                    ? "Confidential Client"
                    : proj.clientDisplayName}
                  {" · "}
                  {proj.role}
                  {" · "}
                  {proj.location}
                </span>
              </div>
              <div className="resume-row-side">
                {formatMonth(proj.startDate)} – {proj.current ? "Present" : formatMonth(proj.endDate)}
              </div>
            </div>
            <ul className="resume-bullets">
              {proj.responsibilities.map((r, j) => (
                <li key={j}>{r}</li>
              ))}
            </ul>
            {proj.standards?.length ? (
              <p className="resume-standards">
                <strong>Codes / Standards:</strong> {proj.standards.join(", ")}
              </p>
            ) : null}
          </div>
        ))}
      </section>

      {/* ============ SCOPE EXPERTISE — Seq 80 ============ */}
      <section className="resume-section">
        <h2 className="resume-section-title">
          <FaLayerGroup className="resume-section-icon" /> Discipline-Specific Experience
        </h2>
        <table className="resume-table">
          <tbody>
            {data.scopeModule.map((row, i) => (
              <tr key={i}>
                <td>{row.field}</td>
                <td className="resume-table-right">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ============ CLIENT APPROVALS — Seq 90 ============ */}
      <section className="resume-section">
        <h2 className="resume-section-title">
          <FaCheckCircle className="resume-section-icon" /> Client Approvals
        </h2>
        {data.clientApprovals.map((a, i) => (
          <div className="resume-row" key={i}>
            <div className="resume-row-main">
              <strong>{a.client}</strong>
              <span className="resume-row-sub">
                {a.role} · {a.scope}
              </span>
            </div>
            <div className="resume-row-side">
              <span className="resume-badge badge-current">{a.status}</span>
              <span className="resume-row-sub">{formatMonth(a.date)}</span>
            </div>
          </div>
        ))}
      </section>

      {/* ============ SKILLS — Seq 100-102 ============ */}
      <section className="resume-section">
        <h2 className="resume-section-title">Skills</h2>

        <div className="resume-subsection">
          <h3>Codes and Standards</h3>
          <div className="resume-tags">
            {data.skills.standards.map((s, i) => (
              <span className="resume-tag" key={i}>{s}</span>
            ))}
          </div>
        </div>

        <div className="resume-subsection">
          <h3>Inspection Equipment</h3>
          <div className="resume-tags">
            {data.skills.equipment.map((s, i) => (
              <span className="resume-tag" key={i}>{s}</span>
            ))}
          </div>
        </div>

        <div className="resume-subsection">
          <h3>Software / Digital Tools</h3>
          <div className="resume-tags">
            {data.skills.software.map((s, i) => (
              <span className="resume-tag" key={i}>{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ LANGUAGES — Seq 110 ============ */}
      <section className="resume-section">
        <h2 className="resume-section-title">
          <FaLanguage className="resume-section-icon" /> Languages
        </h2>
        <table className="resume-table resume-lang-table">
          <thead>
            <tr>
              <th>Language</th>
              <th>Speak</th>
              <th>Read</th>
              <th>Write</th>
            </tr>
          </thead>
          <tbody>
            {data.languages.map((l, i) => (
              <tr key={i}>
                <td>{l.language}</td>
                <td>{l.speak}</td>
                <td>{l.read}</td>
                <td>{l.write}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ============ REFERENCES — Seq 120 ============ */}
      <section className="resume-section">
        <h2 className="resume-section-title">
          <FaAddressCard className="resume-section-icon" /> References
        </h2>
        <p className="resume-summary">{data.references}</p>
      </section>

      {/* ============ DECLARATION — Seq 130 ============ */}
      <section className="resume-section resume-declaration">
        <h2 className="resume-section-title">
          <FaFileSignature className="resume-section-icon" /> Declaration
        </h2>
        <p>{data.declaration}</p>
      </section>
    </div>
  );
});

export default ResumeTemplate;